import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { applyBranding, applyTheme, resolveTheme, type Theme, type ThemePreference } from "../lib/theme";
import type { TenantBranding } from "../lib/types";

interface ThemeCtx {
  theme: Theme;
  preference: ThemePreference;
  setPreference: (p: ThemePreference) => void;
  branding: TenantBranding | null;
}

const Ctx = createContext<ThemeCtx | null>(null);

// Provides theme + branding. The `serverPreference` and `branding` come from the SPA bootstrap
// (the server-side user record + tenants.branding). Changing the preference here optimistically
// re-themes and would PATCH the user record in a full build (persisted server-side, not
// localStorage — the resolution order the spec mandates).
export function ThemeProvider({
  children,
  serverPreference,
  branding,
  onPersistPreference,
}: {
  children: ReactNode;
  serverPreference: ThemePreference | null;
  branding: TenantBranding | null;
  onPersistPreference?: (p: ThemePreference) => void;
}) {
  const [preference, setPref] = useState<ThemePreference>(serverPreference ?? "system");
  const theme = useMemo(() => resolveTheme(preference === "system" ? null : preference), [preference]);

  useEffect(() => {
    applyTheme(theme);
    applyBranding(branding, theme);
  }, [theme, branding]);

  // Re-resolve when the OS scheme changes AND the user is on "system".
  useEffect(() => {
    if (preference !== "system" || !window.matchMedia) return;
    const mq = window.matchMedia("(prefers-color-scheme: light)");
    const handler = () => applyTheme(resolveTheme(null));
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, [preference]);

  const setPreference = (p: ThemePreference) => {
    setPref(p);
    onPersistPreference?.(p); // server-side persistence in a full build
  };

  return <Ctx.Provider value={{ theme, preference, setPreference, branding }}>{children}</Ctx.Provider>;
}

export function useTheme(): ThemeCtx {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useTheme must be used within ThemeProvider");
  return ctx;
}
